import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import CLUB from '../../config/club.js'
import { Card, Pill, money } from './gui.jsx'
import { PRO_SHOP_MODULES } from './gProShopModules.js'
import { IS_GOLF_ELITE } from '../../config/siteMode.js'
import GGStaging from './GGStaging.jsx'
import { watchSales, isToday } from '../../lib/sales.js'
import { watchRepairs } from '../../lib/repairs.js'
import { watchSpecialOrders } from '../../lib/specialOrders.js'
import { watchProducts, isLow } from '../../lib/products.js'
import { watchDemoClubs } from '../../lib/demoClubs.js'
import { watchRentals } from '../../lib/rentals.js'
import { watchLessons } from '../../lib/lessons.js'
import { watchEmployees, clockStatus } from '../../lib/employees.js'
import { watchActivity } from '../../lib/activity.js'

const PRIMARY = CLUB.primaryColor || 'var(--ge-primary)'
const ACCENT = CLUB.accentColor || 'var(--ge-primary)'
const vals = (o) => Object.values(o || {}).filter(Boolean)

export default function GProShopHome() {
  const [sales, setSales] = useState({})
  const [repairs, setRepairs] = useState({})
  const [orders, setOrders] = useState({})
  const [products, setProducts] = useState({})
  const [demos, setDemos] = useState({})
  const [rentals, setRentals] = useState({})
  const [lessons, setLessons] = useState({})
  const [emps, setEmps] = useState({})
  const [activity, setActivity] = useState([])
  useEffect(() => {
    const offs = [watchSales(setSales), watchRepairs(setRepairs), watchSpecialOrders(setOrders), watchProducts(setProducts), watchDemoClubs(setDemos), watchRentals(setRentals), watchLessons(setLessons), watchEmployees(setEmps), watchActivity(setActivity, 12)]
    return () => offs.forEach((o) => o && o())
  }, [])

  const salesToday = vals(sales).filter((s) => isToday(s.timestamp))
  const salesTodayTotal = salesToday.reduce((s, x) => s + (Number(x.total) || 0), 0)
  const repairsWaiting = vals(repairs).filter((r) => !['Picked Up', 'Paid'].includes(r.status)).length
  const ordersWaiting = vals(orders).filter((o) => o.status !== 'Picked Up').length
  const lowStock = vals(products).filter(isLow).length
  const demosOut = vals(demos).filter((d) => !d.returned).length
  const rentalsOut = vals(rentals).filter((r) => r.status === 'Out').length
  const todayISO = new Date().toISOString().slice(0, 10)
  const lessonsToday = vals(lessons).filter((l) => l.scheduledDate === todayISO && l.status === 'Scheduled').length
  const working = vals(emps).filter((e) => clockStatus(e).open).length

  const active = PRO_SHOP_MODULES.filter((m) => m.status === 'active')
  const coming = PRO_SHOP_MODULES.filter((m) => m.status !== 'active')
  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

  const stats = [
    { label: 'Sales Today', value: money(salesTodayTotal), sub: `${salesToday.length} txns`, to: '/admin/gg/pos', icon: '🛒' },
    { label: 'Repairs Waiting', value: repairsWaiting, to: '/admin/gg/repairs', icon: '🔧' },
    { label: 'Orders Waiting', value: ordersWaiting, to: '/admin/special-orders', icon: '📦' },
    { label: 'Inventory Alerts', value: lowStock, to: '/admin/gg/inventory', icon: '🏷️', alert: lowStock > 0 },
    { label: 'Demo Clubs Out', value: demosOut, to: '/admin/gg/demo-clubs', icon: '🏌️' },
    { label: 'Rentals Out', value: rentalsOut, to: '/admin/gg/rentals', icon: '🛍️' },
  ]

  return (
    <div className="space-y-5">
      {/* Top bar greeting */}
      <div className="rounded-2xl p-5 flex items-center justify-between gap-3 flex-wrap" style={{ background: PRIMARY, color: '#fff', border: `1px solid ${ACCENT}55` }}>
        <div>
          <div className="text-[11px] font-extrabold uppercase tracking-[0.28em]" style={{ color: ACCENT }}>{IS_GOLF_ELITE ? 'Pro Shop' : 'Pro Shop Operations'}</div>
          <h2 className="text-2xl font-extrabold" style={IS_GOLF_ELITE ? undefined : { fontFamily: 'Cinzel, serif' }}>{greeting}</h2>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.75)' }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>
        <div className="text-right text-sm flex items-center gap-2">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1" style={{ background: 'rgba(255,255,255,0.12)' }}>
            <span>⛅ 72°F</span><span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.6)' }}>weather hookup</span>
          </div>
          <Link to="/admin/gg/reports" className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: ACCENT, color: PRIMARY }}>📊 Reports</Link>
          <Link to="/admin/gg/settings" className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}>⚙ Settings</Link>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {stats.map((s) => (
          <Link key={s.label} to={s.to}>
            <Card style={s.alert ? { borderColor: '#F9731566' } : {}} className="h-full">
              <div className="text-xl">{s.icon}</div>
              <div className="text-2xl font-extrabold mt-1" style={{ color: s.alert ? '#C2410C' : PRIMARY }}>{s.value}</div>
              <div className="text-[11px] text-gray-500">{s.label}{s.sub ? ` · ${s.sub}` : ''}</div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Today at a glance + Recent activity */}
      <div className="grid md:grid-cols-2 gap-3">
        <Card>
          <div className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: PRIMARY }}>Today at a Glance</div>
          <Glance label="Tee Times" value="—" note="tee-sheet hookup" />
          <Glance label="Rounds Expected" value="—" note="tee-sheet hookup" />
          <Glance label="Events Today" value="—" note="tournament hookup" />
          <Glance label="Lessons Scheduled" value={lessonsToday} />
          <Glance label="Employees Working" value={working} />
        </Card>
        <Card>
          <div className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: PRIMARY }}>Recent Activity</div>
          {activity.length === 0 ? <p className="text-sm text-gray-400">No activity yet.</p> : (
            <div className="space-y-1 text-sm max-h-64 overflow-y-auto">
              {activity.map((a) => (
                <div key={a.id} className="flex items-center gap-2 border-b py-0.5" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>
                  <span>{a.icon || '•'}</span><span className="flex-1 truncate">{a.text}</span>
                  <span className="text-[10px] text-gray-400 shrink-0">{a.timestamp ? new Date(a.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Module tiles */}
      <section>
        <Rule label="Modules" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          {active.map((m) => <Tile key={m.key} m={m} />)}
        </div>
      </section>
      <section>
        <Rule label="Coming" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          {coming.map((m) => <Tile key={m.key} m={m} />)}
        </div>
      </section>

      <details className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${ACCENT}44`, background: CLUB.creamColor }}>
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-bold" style={{ color: PRIMARY }}>🧪 Golf Genius Sandbox (legacy G- features)</summary>
        <div className="p-4 border-t" style={{ borderColor: `${ACCENT}33` }}><GGStaging /></div>
      </details>
    </div>
  )
}

function Glance({ label, value, note }) {
  return <div className="flex items-center justify-between border-b py-1 text-sm" style={{ borderColor: 'rgba(0,0,0,0.05)' }}><span className="text-gray-600">{label}{note ? <span className="text-[10px] text-gray-400 ml-1">({note})</span> : null}</span><span className="font-extrabold" style={{ color: PRIMARY }}>{value}</span></div>
}
function Rule({ label }) {
  return <div className="flex items-center gap-3"><span className="h-px w-6" style={{ background: ACCENT }} /><span className="text-xs font-extrabold uppercase tracking-widest" style={{ color: PRIMARY }}>{label}</span><span className="h-px flex-1" style={{ background: `${ACCENT}55` }} /></div>
}
function Tile({ m }) {
  const isActive = m.status === 'active'
  const inner = (
    <>
      <div className="flex items-start justify-between gap-2"><span className="text-2xl leading-none">{m.icon}</span>{isActive ? <Pill bg={`${ACCENT}22`} fg="var(--ge-primary)">Open</Pill> : <Pill>Coming</Pill>}</div>
      <div className="mt-2 text-base font-extrabold" style={{ color: isActive ? PRIMARY : '#6B7280' }}>{m.label}</div>
      {m.desc ? <div className="text-xs mt-0.5 leading-snug" style={{ color: isActive ? '#4B5563' : '#9CA3AF' }}>{m.desc}</div> : null}
    </>
  )
  return isActive
    ? <Link to={m.to} className="rounded-2xl p-4 h-full block" style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}55`, boxShadow: '0 6px 16px rgba(0,0,0,0.06)' }}>{inner}</Link>
    : <div className="rounded-2xl p-4 h-full" style={{ background: '#FAFAF7', border: '1px dashed rgba(0,0,0,0.12)', opacity: 0.75 }}>{inner}</div>
}
